import { api, req } from "../api.ts";
import { esc, h } from "../dom.ts";
import { readPlayer } from "../play/client.ts";
import { playPage } from "./play.ts";
import { emailSignIn } from "./emailSignIn.ts";
import { playerPreferences, signOutControl } from "../play/profile.ts";

interface Entry { id:string; player_id:string; white_name:string; black_name:string; lifecycle:string; saved:boolean; retained:boolean; expires_at:number|null; abandon_at:number|null }
export function saveGamesPanel(done?: () => void, game?: {id:string;player_id:string}): HTMLElement {
  const panel = h(`<details class="club-save"><summary>Save my games</summary><p>Keep your games and recover them in another browser with your email. Your opponent can stay a guest.</p><div data-email></div><p class="msg" role="status"></p></details>`);
  void readPlayer().then(async player => {
    if (player?.kind === "account") {
      panel.querySelector("p")!.textContent = "Your email can recover games saved to your library.";
      if (!game) { panel.querySelector("[data-email]")!.innerHTML = '<p>Use Save game beside each game below.</p>'; return; }
      const button = h('<button class="btn primary">Save this game</button>') as HTMLButtonElement;
      button.onclick = async () => {
        button.disabled = true;
        try {
          await req("POST",`/api/play/games/${game.id}/save`,{actor_id:game.player_id});
          panel.querySelector(".msg")!.textContent = "Saved to My games."; button.remove(); done?.();
        } catch(error) { panel.querySelector(".msg")!.textContent = error instanceof Error ? error.message : "Please retry."; button.disabled = false; }
      };
      panel.querySelector("[data-email]")!.append(button); return;
    }
    const config = await api.getConfig();
    if (!config.email_signin_enabled) { panel.querySelector(".msg")!.textContent = "Email-code access is currently disabled on this server. You can keep playing in this browser."; return; }
    panel.querySelector("[data-email]")!.append(emailSignIn(config.registration_mode === "public",{saveGames:true,onSuccess:() => {
      panel.querySelector("[data-email]")!.replaceChildren();
      panel.querySelector(".msg")!.textContent = "Saved to My games. Your email can recover them in another browser.";
      done?.();
    }}));
  }).catch(() => { panel.querySelector(".msg")!.textContent = "Could not check email access. Reload to try again."; });
  return panel;
}
export async function myGamesScreen(): Promise<HTMLElement> {
  let player = await readPlayer();
  const root = playPage(`<h1>My games</h1><p data-identity></p><div data-save></div><div data-games></div><button class="btn ghost" data-more hidden>More games</button><p class="msg" role="status" aria-live="polite"></p>`,"games");
  let offset = 0; let loading = false;
  const more = root.querySelector<HTMLButtonElement>("[data-more]")!;
  root.querySelector('main')!.append(playerPreferences());
  if(player)root.querySelector('main')!.append(signOutControl());
  root.querySelector("[data-save]")!.append(saveGamesPanel(() => { offset=0; root.querySelector("[data-games]")!.replaceChildren(); void load(); }));
  async function load() {
    if (loading) return; loading = true; more.disabled = true;
    try {
      player = await readPlayer();
      root.querySelector("[data-identity]")!.textContent = player ? player.kind === "account" ? `Signed in as ${player.username}.` : "Playing as a guest. Keep this browser's cookies to return to your games." : "Use your email to recover saved games. A new guest game needs no account.";
      if (!player) return;
      const result = await req<{games:Entry[];has_more:boolean}>("GET",`/api/play/games?offset=${offset}`);
      const list = root.querySelector<HTMLElement>("[data-games]")!;
      if (!result.games.length && offset === 0) list.innerHTML = '<p>No games yet. <a data-link href="/">Invite someone to play.</a></p>';
      for (const game of result.games) {
        const retention = game.saved ? "Saved to My games" : game.expires_at ? `Temporary · available until ${new Date(game.expires_at*1000).toLocaleString()}` : game.retained ? "Retained by a participant; not yet saved to your library" : "Temporary · unsaved games end after seven days without a move or resume";
        const row = h(`<article class="club-game"><h2><a data-link href="/game/${esc(game.id)}">${esc(game.white_name)} · ${esc(game.black_name)}</a></h2><p>${esc(game.lifecycle)} · ${esc(retention)}</p><a class="btn ghost" href="/api/play/games/${esc(game.id)}/pgn" download>Download PGN</a>${player.kind === "account" && !game.saved ? '<button class="btn" data-save-game>Save game</button>' : ""}</article>`);
        const button = row.querySelector<HTMLButtonElement>("[data-save-game]");
        if (button) button.onclick = async () => {
          button.disabled = true;
          try { await req("POST",`/api/play/games/${game.id}/save`,{actor_id:game.player_id}); row.querySelector("p")!.textContent = `${game.lifecycle} · Saved to My games`; button.remove(); }
          catch (error) { root.querySelector(".msg")!.textContent = error instanceof Error ? error.message : "Please retry."; button.disabled = false; }
        };
        list.append(row);
      }
      offset += result.games.length; more.hidden = !result.has_more;
    } catch (error) { root.querySelector(".msg")!.textContent = error instanceof Error ? error.message : "Please retry."; }
    finally { loading = false; more.disabled = false; }
  }
  more.onclick = () => void load(); await load(); return root;
}
